package com.eladfinish.bible.contest.screens.openQuestions

import androidx.compose.ui.graphics.Color
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.common.usecases.GetHighlightedVerseMarkupRangeUseCase
import com.eladfinish.bible.contest.common.usecases.MakeOtiotPasukSmallerUseCase
import com.eladfinish.bible.contest.common.usecases.SendReportToGoogleFormUseCaseKMP
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.currentMilliseconds
import com.eladfinish.bible.contest.data.ChapterData
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.domain.BooksRepository.bookNamesHebrew
import com.eladfinish.bible.contest.extensions.convertToDataClass
import com.eladfinish.bible.contest.extensions.formatString
import com.eladfinish.bible.contest.navigation.NavigationCommand
import com.eladfinish.bible.contest.navigation.SearchScreenNavigationDestination
import com.eladfinish.bible.contest.network.error.utils.isInternetError
import com.eladfinish.bible.contest.open_questions_refreshing_questions
import com.eladfinish.bible.contest.open_questions_refreshing_topics
import com.eladfinish.bible.contest.screens.openQuestions.OpenQuestionsStore.Companion.CACHE_TTL_MS
import com.eladfinish.bible.contest.screens.openQuestions.model.QuestionItem
import com.eladfinish.bible.contest.screens.openQuestions.model.QuestionItemJsonVisibility
import com.eladfinish.bible.contest.screens.openQuestions.model.TopicItem
import com.eladfinish.bible.contest.screens.openQuestions.usecases.BuildNumbersByBooksQuestionsUseCase
import com.eladfinish.bible.contest.screens.openQuestions.usecases.BuildQuestionsFromSheetUseCase
import com.eladfinish.bible.contest.screens.openQuestions.usecases.GetGoogleDriveDataUseCase
import com.eladfinish.bible.contest.screens.openQuestions.usecases.GetGoogleScriptDataUseCase
import com.eladfinish.bible.contest.screens.openQuestions.usecases.GetQuestionItemJsonListUseCase
import com.eladfinish.bible.contest.screens.openQuestions.usecases.GetQuestionsAndAnswersUseCase
import com.eladfinish.bible.contest.screens.openQuestions.usecases.GetTopicWithLinksUseCase
import com.eladfinish.bible.contest.screens.openQuestions.usecases.GetTopicsUseCase
import com.eladfinish.bible.contest.screens.openQuestions.usecases.TurnPairsToOpenQuestionsUseCase
import com.eladfinish.bible.contest.screens.openQuestions.usecases.ValidateEmailUseCase
import com.eladfinish.bible.contest.screens.pairMatching.usecases.GetPairsUseCase
import com.eladfinish.bible.contest.screens.randomVerse.usecases.GenerateMultipleRandomVersesUseCase
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannedRange
import com.eladfinish.bible.contest.ui.models.AlternateRowData
import com.eladfinish.bible.contest.utils.SheetUtil
import com.eladfinish.bible.contest.utils.gematriaWord
import com.eladfinish.bible.contest.utils.spreadsheet.getId
import com.russhwolf.settings.Settings
import dev.icerock.moko.resources.StringResource
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.flow.MutableStateFlow

class OpenQuestionsStore(
    private val settings: Settings,
    private val getTopicsUseCase: GetTopicsUseCase,
    private val getQuestionsAndAnswersUseCase: GetQuestionsAndAnswersUseCase,
    private val getPairsUseCase: GetPairsUseCase,
    private val turnPairsToOpenQuestions: TurnPairsToOpenQuestionsUseCase,
    private val assetReader: AssetReader,
    private val getHighlightedVerseMarkupRangeUseCase: GetHighlightedVerseMarkupRangeUseCase,
    private val makeOtiotPasukSmallerUseCase: MakeOtiotPasukSmallerUseCase,
    private val sendReportToGoogleFormUseCase: SendReportToGoogleFormUseCaseKMP,
    private val validateEmailUseCase: ValidateEmailUseCase,
    private val getGoogleScriptDataUseCase: GetGoogleScriptDataUseCase,
    private val getGoogleDriveDataUseCase: GetGoogleDriveDataUseCase,
    private val buildQuestionsFromSheetUseCase: BuildQuestionsFromSheetUseCase,
    private val getTopicWithLinksUseCase: GetTopicWithLinksUseCase,
    private val getQuestionItemJsonListUseCase: GetQuestionItemJsonListUseCase,
    private val buildNumbersByBooksQuestionsUseCase: BuildNumbersByBooksQuestionsUseCase,
    private val isMultipleRandomVersesMode: Boolean,
    private val generateMultipleRandomVersesUseCase: GenerateMultipleRandomVersesUseCase,
) : ViewModelStore<OpenQuestionsState, OpenQuestionsAction, OpenQuestionsEffect>(
    tag = "OpenQuestionsStore",
) {

    /** Fetches the questions on screen again; set by whichever loader put them there. */
    private var reloadShownQuestions: (() -> Unit)? = null

    private val defaultState: OpenQuestionsState
        get() = OpenQuestionsState(
            allQuestions = emptyList<String>().toImmutableList(),
            topicItemList = loadCachedTopics(),
            predefinedTopics = emptyList<String>().toImmutableList(),
            sheetNumbers = emptyList<Pair<Int, String>>().toImmutableList(),
            dialogState = OpenQuestionsState.DialogState.None,
        )

    override val stateFlow = MutableStateFlow(defaultState)

    override fun handleAction(action: OpenQuestionsAction): OpenQuestionsState =
        when (action) {
            is OpenQuestionsAction.Initialize -> handleInitialize()
            is OpenQuestionsAction.LoadTopics -> handleLoadTopics(askedByUser = true)
            is OpenQuestionsAction.ReloadShownQuestions -> handleReloadShownQuestions()
            is OpenQuestionsAction.TopicFromDialogSelected -> handleTopicFromDialogSelected(action.index)
            is OpenQuestionsAction.TopicSelected -> handleTopicSelected(action.topic)
            is OpenQuestionsAction.LinkTopicSelected -> handleLinkTopicSelected(action.url, action.sheetId, action.topicName)
            is OpenQuestionsAction.SheetNumberSelected -> handleSheetNumberSelected(action.sheetId, action.topicName)
            is OpenQuestionsAction.LoadPairsAsQuestions -> handleLoadPairsAsQuestions()
            is OpenQuestionsAction.LinkEntrySelected -> handleLinkEntrySelected(action.entry, action.type)
            is OpenQuestionsAction.QuestionsLoaded -> handleQuestionsLoaded(action.items, action.title)
            is OpenQuestionsAction.ToggleAnswerVisibility -> handleToggleAnswerVisibility(action.index)
            is OpenQuestionsAction.LoadMoreQuestions -> handleLoadMoreQuestions()
            is OpenQuestionsAction.RefreshQuestions -> handleRefreshQuestions()
            is OpenQuestionsAction.CopyQuestion -> handleCopyQuestion(action.index)
            is OpenQuestionsAction.ShowChapterForQuestion -> handleShowChapterForQuestion(action.index)
            is OpenQuestionsAction.SearchRegexForQuestion -> handleSearchRegexForQuestion(action.index)
            is OpenQuestionsAction.AddQuestion -> handleAddQuestion()
            is OpenQuestionsAction.AddQuestionFieldChanged -> handleAddQuestionFieldChanged(action.field, action.value)
            is OpenQuestionsAction.SubmitAddQuestion -> handleSubmitAddQuestion()
            is OpenQuestionsAction.CreateYourOwnQnA -> handleCreateYourOwnQnA()
            is OpenQuestionsAction.CustomSheetUrlChanged -> handleCustomSheetUrlChanged(action.url)
            is OpenQuestionsAction.SubmitCustomSheetUrl -> handleSubmitCustomSheetUrl()
            is OpenQuestionsAction.CustomSheetPositionSelected -> handleCustomSheetPositionSelected(action.url, action.position)
            is OpenQuestionsAction.HelpClicked -> handleHelpClicked()
            is OpenQuestionsAction.ToggleHelp -> handleToggleHelp(action.shouldShowNextTime)
            is OpenQuestionsAction.HideDialog -> handleHideDialog()
            is OpenQuestionsAction.ShowTableOfContents -> handleShowTableOfContents(action.items)
            is OpenQuestionsAction.ShowChapterDialog -> handleShowChapterDialog(action.chapterData)
            is OpenQuestionsAction.ShowGeneralMessage -> handleShowGeneralMessage(action.title, action.content)
        }

    // ─── Topic loading ───────────────────────────────────────────────────────

    private var didInitialize = false

    private fun handleInitialize(): OpenQuestionsState {
        if (isMultipleRandomVersesMode) {
            // A rotation runs this again: the verses on screen stay, with the answers revealed so far.
            if (currentState.questionItems.isNotEmpty()) return currentState
            launchEffect {
                var emptyMikud = false
                val verses = generateMultipleRandomVersesUseCase(
                    onEmptyMikud = { emptyMikud = true },
                )
                if (emptyMikud) {
                    sideEffectFlow.emit(OpenQuestionsEffect.ShowToast("ישנה בעיה בהצגת פסוקים אקראיים! (מיקוד ריק)"))
                }
                if (verses.isNotEmpty()) {
                    val items = verses.mapIndexed { i, v -> v.toQuestionItem(num = i + 1) }
                    updateState {
                        it.copy(
                            isMultipleRandomVersesMode = true,
                            questionItems = items.toImmutableList(),
                            topicTitle = MULTIPLE_RANDOM_VERSES_TITLE,
                            questionsShownCount = items.size,
                        )
                    }
                }
            }
        } else {
            // A rotation runs this again: the list is refreshed, but the topics dialog must not replace
            // what is on screen now, or come back after the user closed it.
            val isFirstEntry = !didInitialize
            didInitialize = true
            return handleLoadTopics(mayOpenTopicsDialog = isFirstEntry)
        }
        return currentState
    }

    /**
     * Pull to refresh: fetches the questions on screen again, from wherever they came, and stays
     * on them. With no questions on screen there is nothing to reload, so it refreshes the topics.
     */
    private fun handleReloadShownQuestions(): OpenQuestionsState {
        // In random-verses mode this regenerates the cocktail
        if (isMultipleRandomVersesMode) {
            showToast(SharedRes.strings.open_questions_refreshing_questions)
            return handleInitialize()
        }

        val reload = reloadShownQuestions
        if (reload != null && currentState.questionItems.isNotEmpty()) {
            showToast(SharedRes.strings.open_questions_refreshing_questions)
            reload()
            return currentState
        }
        showToast(SharedRes.strings.open_questions_refreshing_topics)
        // Invalidate the cache timestamp so handleLoadTopics always fetches from server
        settings.putLong(Constants.PREF_OPEN_QUESTIONS_TOPICS_LAST_FETCH_EPOCH_MS, 0L)
        return handleLoadTopics()
    }

    /**
     * [askedByUser] is true for the toolbar's "table of contents" and "refresh": the picker opens
     * even over the questions on screen. The store's own reloads (entering the screen) must not
     * interrupt a user who is reading questions, so they leave it closed then.
     */
    private fun handleLoadTopics(askedByUser: Boolean = false, mayOpenTopicsDialog: Boolean = true): OpenQuestionsState {
        launchEffect {
            val cachedTopics = currentState.topicItemList
            val alreadyViewingQuestions = !askedByUser && (currentState.questionItems.isNotEmpty() || !mayOpenTopicsDialog)

            if (cachedTopics.isNotEmpty() && isTopicCacheFresh()) {
                // Cache is valid. Only show the topics dialog on first entry;
                // if the user is already viewing questions (e.g. returning from search),
                // silently update the list without interrupting them.
                if (!alreadyViewingQuestions) {
                    updateState {
                        it.copy(
                            dialogState = OpenQuestionsState.DialogState.TableOfContents(
                                tableOfContentsItems = buildTopicsDialogItems(cachedTopics.toList()),
                            ),
                        )
                    }
                }
                return@launchEffect
            }

            updateState { it.copy(isLoading = true) }
            getTopicsUseCase.invoke().fold(
                onSuccess = { json ->
                    settings.putString(Constants.PREF_OPEN_QUESTIONS_TOPICS_JSON, json)
                    settings.putLong(
                        Constants.PREF_OPEN_QUESTIONS_TOPICS_LAST_FETCH_EPOCH_MS,
                        currentMilliseconds,
                    )
                    val topics = json.convertToDataClass<List<TopicItem>>()
                    // Same rule: only show the dialog if no topic is currently displayed
                    updateState {
                        it.copy(
                            isLoading = false,
                            topicItemList = topics.toImmutableList(),
                            dialogState = if (!alreadyViewingQuestions) {
                                OpenQuestionsState.DialogState.TableOfContents(
                                    tableOfContentsItems = buildTopicsDialogItems(topics),
                                )
                            } else {
                                it.dialogState
                            },
                        )
                    }
                },
                onFailure = { error ->
                    updateState { it.copy(isLoading = false) }
                    // On failure, fall back to the cached list only if no topic is displayed
                    if (cachedTopics.isNotEmpty() && !alreadyViewingQuestions) {
                        updateState {
                            it.copy(
                                dialogState = OpenQuestionsState.DialogState.TableOfContents(
                                    tableOfContentsItems = buildTopicsDialogItems(cachedTopics.toList()),
                                ),
                            )
                        }
                    }
                    if (error.isInternetError()) {
                        sideEffectFlow.emit(OpenQuestionsEffect.ShowNoInternetError)
                    } else {
                        sideEffectFlow.emit(OpenQuestionsEffect.ShowToast(error.message.orEmpty()))
                    }
                },
            )
        }
        return currentState
    }

    /** Returns true if the cached topics were fetched less than [CACHE_TTL_MS] ago. */
    private fun isTopicCacheFresh(): Boolean {
        val lastFetch = settings.getLong(
            key = Constants.PREF_OPEN_QUESTIONS_TOPICS_LAST_FETCH_EPOCH_MS,
            defaultValue = 0L,
        )
        if (lastFetch == 0L) return false
        val ageMs = currentMilliseconds - lastFetch
        return ageMs < CACHE_TTL_MS
    }

    /**
     * Resolves a dialog row index to the correct action.
     * Row 0 is always the "Questions from pair matching" predefined entry.
     * Rows 1+ map to (topicItemList) by (index - PREDEFINED_ITEMS_COUNT).
     */
    private fun handleTopicFromDialogSelected(index: Int): OpenQuestionsState =
        when (index) {
            PAIRS_ITEM_INDEX -> handleLoadPairsAsQuestions()
            else -> {
                val topicIndex = index - PREDEFINED_ITEMS_COUNT
                val topic = currentState.topicItemList.getOrNull(topicIndex)
                if (topic != null) handleTopicSelected(topic) else currentState
            }
        }

    private fun handleTopicSelected(topic: TopicItem): OpenQuestionsState {
        launchEffect {
            updateState { it.copy(isLoading = true, currentTopic = topic) }
            when {
                topic.items.isNotEmpty() -> {
                    updateState {
                        it.copy(
                            isLoading = false,
                            sheetNumbers = topic.items
                                .mapIndexed { i, name -> Pair(i, name) }
                                .toImmutableList(),
                            dialogState = OpenQuestionsState.DialogState.TableOfContents(
                                tableOfContentsItems = topic.items
                                    .map { name -> AlternateRowData(title = name) }
                                    .toImmutableList(),
                            ),
                        )
                    }
                }

                else -> {
                    val specialType = specialTopicType(topic.topic)
                    if (specialType != null) {
                        loadSpecialTopicLinks(topic = topic, type = specialType)
                    } else {
                        loadQuestionsForTopic(
                            spreadsheetId = Constants.google_spreadsheet_open_questions_topics.getId(),
                            sheetId = topic.id,
                            topicName = topic.topic,
                        )
                    }
                }
            }
        }
        return currentState
    }

    /**
     * Returns the [SpecialTopicType] if the topic name matches one of the known
     * link-based topics, or null for regular Q&A topics.
     * Uses `contains` because topic names may be prefixed with a number and/or
     * suffixed with an asterisk.
     */
    private fun specialTopicType(topicName: String): SpecialTopicType? = when {
        topicName.contains(SPECIAL_TOPIC_NUMBERS_BY_BOOKS) -> SpecialTopicType.NUMBERS_BY_BOOKS
        topicName.contains(SPECIAL_TOPIC_EDUCATION_MINISTRY) -> SpecialTopicType.EDUCATION_MINISTRY
        else -> null
    }

    private suspend fun loadSpecialTopicLinks(topic: TopicItem, type: SpecialTopicType) {
        getTopicWithLinksUseCase.invoke(
            url = Constants.google_spreadsheet_open_questions_topics,
            sheetId = topic.id,
        ).fold(
            onSuccess = { (entries, bookNames) ->
                updateState {
                    it.copy(
                        isLoading = false,
                        pendingLinkEntries = entries.toImmutableList(),
                        pendingSpecialTopicType = type,
                        dialogState = OpenQuestionsState.DialogState.TableOfContents(
                            tableOfContentsItems = bookNames
                                .map { name -> AlternateRowData(title = name) }
                                .toImmutableList(),
                        ),
                    )
                }
            },
            onFailure = { error ->
                updateState { it.copy(isLoading = false) }
                if (error.isInternetError()) {
                    sideEffectFlow.emit(OpenQuestionsEffect.ShowNoInternetError)
                } else {
                    sideEffectFlow.emit(OpenQuestionsEffect.ShowToast(error.message.orEmpty()))
                }
            },
        )
    }

    private fun handleLinkEntrySelected(
        entry: com.eladfinish.bible.contest.screens.openQuestions.model.LinkTopicItemEntry,
        type: SpecialTopicType,
    ): OpenQuestionsState {
        launchEffect {
            updateState {
                it.copy(
                    isLoading = true,
                    pendingLinkEntries = kotlinx.collections.immutable.persistentListOf(),
                    pendingSpecialTopicType = null
                )
            }
            val spreadsheetId = entry.link.getId()
            val sheetId = entry.id
            when (type) {
                SpecialTopicType.NUMBERS_BY_BOOKS -> {
                    getQuestionItemJsonListUseCase(spreadsheetId, sheetId).fold(
                        onSuccess = { jsons ->
                            val items = buildNumbersByBooksQuestionsUseCase(jsons)
                            val title = "נושא: *מספרים בספר ${entry.book}* (${items.size} שאלות)"
                            reloadShownQuestions = { handleLinkEntrySelected(entry, type) }
                            updateState {
                                it.copy(
                                    isLoading = false,
                                    questionItems = items.toImmutableList(),
                                    topicTitle = title,
                                    questionsShownCount = items.size,
                                    dialogState = OpenQuestionsState.DialogState.None,
                                )
                            }
                            sideEffectFlow.emit(OpenQuestionsEffect.ScrollToTop)
                        },
                        onFailure = { error ->
                            updateState { it.copy(isLoading = false) }
                            if (error.isInternetError()) sideEffectFlow.emit(OpenQuestionsEffect.ShowNoInternetError)
                            else sideEffectFlow.emit(OpenQuestionsEffect.ShowToast(error.message.orEmpty()))
                        },
                    )
                }

                SpecialTopicType.EDUCATION_MINISTRY -> {
                    loadQuestionsForTopic(
                        spreadsheetId = spreadsheetId,
                        sheetId = sheetId,
                        topicName = entry.book,
                    )
                }
            }
        }
        return currentState
    }

    private fun handleLinkTopicSelected(
        url: String,
        sheetId: String,
        topicName: String,
    ): OpenQuestionsState {
        launchEffect {
            updateState { it.copy(isLoading = true) }
            loadQuestionsForTopic(
                spreadsheetId = url.getId(),
                sheetId = sheetId,
                topicName = topicName,
            )
        }
        return currentState
    }

    private fun handleSheetNumberSelected(
        sheetId: String,
        topicName: String,
    ): OpenQuestionsState {
        launchEffect {
            updateState { it.copy(isLoading = true) }
            loadQuestionsForTopic(
                spreadsheetId = Constants.google_spreadsheet_open_questions_topics.getId(),
                sheetId = sheetId,
                topicName = topicName,
            )
        }
        return currentState
    }

    // ─── Questions ───────────────────────────────────────────────────────────

    private fun handleLoadPairsAsQuestions(): OpenQuestionsState {
        launchEffect {
            updateState { it.copy(isLoading = true) }
            getPairsUseCase.invoke().fold(
                onSuccess = { json ->
                    val items = turnPairsToOpenQuestions(data = json)
                    reloadShownQuestions = { handleLoadPairsAsQuestions() }
                    updateState {
                        it.copy(
                            isLoading = false,
                            questionItems = items.toImmutableList(),
                            topicTitle = "",
                            questionsShownCount = items.size,
                        )
                    }
                    sideEffectFlow.emit(OpenQuestionsEffect.ScrollToTop)
                },
                onFailure = { error ->
                    updateState { it.copy(isLoading = false) }
                    if (error.isInternetError()) {
                        sideEffectFlow.emit(OpenQuestionsEffect.ShowNoInternetError)
                    }
                },
            )
        }
        return currentState
    }

    private fun handleQuestionsLoaded(
        items: ImmutableList<QuestionItem>,
        title: String,
    ): OpenQuestionsState =
        currentState.copy(
            isLoading = false,
            questionItems = items,
            topicTitle = title,
            questionsShownCount = items.size,
        )

    private fun handleToggleAnswerVisibility(index: Int): OpenQuestionsState {
        val items = currentState.questionItems
        if (index !in items.indices) return currentState
        return currentState.copy(
            questionItems = items.mapIndexed { i, item ->
                if (i == index) item.withToggledVisibility() else item
            }.toImmutableList(),
        )
    }

    private fun handleLoadMoreQuestions(): OpenQuestionsState = currentState

    /**
     * Refresh pulls the topics again from the spreadsheet, past their daily cache, and opens the
     * topics picker. Picking a topic then loads its questions, which are never cached.
     */
    private fun handleRefreshQuestions(): OpenQuestionsState {
        // In random-verses mode, Refresh regenerates the cocktail
        if (isMultipleRandomVersesMode) {
            showToast(SharedRes.strings.open_questions_refreshing_questions)
            return handleInitialize()
        }

        showToast(SharedRes.strings.open_questions_refreshing_topics)
        settings.putLong(Constants.PREF_OPEN_QUESTIONS_TOPICS_LAST_FETCH_EPOCH_MS, 0L)
        return handleLoadTopics(askedByUser = true)
    }

    private fun showToast(message: StringResource) {
        launchEffect { sideEffectFlow.emit(OpenQuestionsEffect.ShowToastRes(message)) }
    }

    // ─── Long-press context menu ──────────────────────────────────────────────

    private fun handleCopyQuestion(index: Int): OpenQuestionsState {
        val item = currentState.questionItems.getOrNull(index) ?: return currentState
        launchEffect {
            sideEffectFlow.emit(OpenQuestionsEffect.CopyToClipboard(item.toDisplayString()))
        }
        return currentState
    }

    private fun handleShowChapterForQuestion(index: Int): OpenQuestionsState {
        val item = currentState.questionItems.getOrNull(index) ?: return currentState
        if (item.book.isEmpty()) {
            launchEffect { sideEffectFlow.emit(OpenQuestionsEffect.ShowToast("לא הוזן המקור לשאלה זו")) }
            return currentState
        }
        if (item.chapter.isEmpty()) {
            launchEffect { sideEffectFlow.emit(OpenQuestionsEffect.ShowToast("ספר: ${item.book} (לא הוזן פרק)")) }
            return currentState
        }

        launchEffect {
            val bookIndex = bookNamesHebrew.indexOf(item.book)
            if (bookIndex == -1) {
                sideEffectFlow.emit(OpenQuestionsEffect.ShowToast("לא נמצא הספר: ${item.book}"))
                return@launchEffect
            }

            val (content, markupRange) = getHighlightedVerseMarkupRangeUseCase(
                bookIndex = bookIndex,
                chapter = item.chapter,
                otVerse = item.verse.ifEmpty { null },
            )

            val spannedRangeList = buildList {
                addAll(makeOtiotPasukSmallerUseCase(charSequence = content))
                add(SpannedRange(spanType = SpanType.BackgroundColor(Color.Yellow), textRange = markupRange))
            }

            val chapterData = ChapterData(
                bookIndex = bookIndex,
                chapterIndex = item.chapter.gematriaWord(),
                content = content,
                spannedRangeList = spannedRangeList.toImmutableList(),
            )
            updateState {
                it.copy(dialogState = OpenQuestionsState.DialogState.ChapterDialog(chapterData))
            }
        }
        return currentState
    }

    private fun handleSearchRegexForQuestion(index: Int): OpenQuestionsState {
        val item = currentState.questionItems.getOrNull(index) ?: return currentState
        if (item.regex.isEmpty()) return currentState
        launchEffect {
            sideEffectFlow.emit(
                OpenQuestionsEffect.NavigateToSearch(
                    navigation = SearchScreenNavigationDestination(text = item.regex)
                        .let(NavigationCommand::Navigate)
                )
            )
        }
        return currentState
    }

    // ─── Add Question (submit to developer's Google Form) ────────────────────

    private fun handleAddQuestion(): OpenQuestionsState =
        currentState.copy(
            dialogState = OpenQuestionsState.DialogState.AddQuestion(
                firstName = settings.getString(key = Constants.PREF_FIRST_NAME, defaultValue = ""),
                lastName = settings.getString(key = Constants.PREF_LAST_NAME, defaultValue = ""),
                email = settings.getString(key = Constants.PREF_USER_EMAIL, defaultValue = ""),
            )
        )

    private fun handleAddQuestionFieldChanged(
        field: AddQuestionFormField,
        value: String,
    ): OpenQuestionsState {
        val current = currentState.dialogState as? OpenQuestionsState.DialogState.AddQuestion
            ?: return currentState
        return currentState.copy(
            dialogState = when (field) {
                AddQuestionFormField.QUESTION -> current.copy(question = value)
                AddQuestionFormField.ANSWER -> current.copy(answer = value)
                AddQuestionFormField.FIRST_NAME -> current.copy(firstName = value)
                AddQuestionFormField.LAST_NAME -> current.copy(lastName = value)
                AddQuestionFormField.EMAIL -> current.copy(email = value)
            }
        )
    }

    private fun handleSubmitAddQuestion(): OpenQuestionsState {
        val form = currentState.dialogState as? OpenQuestionsState.DialogState.AddQuestion
            ?: return currentState

        if (form.question.isBlank() || form.answer.isBlank()) {
            launchEffect { sideEffectFlow.emit(OpenQuestionsEffect.ShowToast("שאלה ותשובה הם שדות חובה")) }
            return currentState
        }
        if (!validateEmailUseCase(form.email)) {
            launchEffect { sideEffectFlow.emit(OpenQuestionsEffect.ShowToast("יש להזין כתובת דוא\"ל תקינה")) }
            return currentState
        }

        launchEffect {
            updateState {
                it.copy(
                    dialogState = (it.dialogState as? OpenQuestionsState.DialogState.AddQuestion)
                        ?.copy(isSubmitting = true) ?: it.dialogState
                )
            }
            if (form.firstName.isNotBlank()) settings.putString(Constants.PREF_FIRST_NAME, form.firstName.trim())
            if (form.lastName.isNotBlank()) settings.putString(Constants.PREF_LAST_NAME, form.lastName.trim())
            if (form.email.isNotBlank()) settings.putString(Constants.PREF_USER_EMAIL, form.email.trim())

            sendReportToGoogleFormUseCase(
                id = Constants.google_form_open_questions_ID,
                keys = listOf(ADD_Q_KEY_QUESTION, ADD_Q_KEY_ANSWER, ADD_Q_KEY_FIRST_NAME, ADD_Q_KEY_LAST_NAME, ADD_Q_KEY_EMAIL),
                values = listOf(
                    form.question.trim(),
                    form.answer.trim(),
                    form.firstName.trim().ifBlank { "_" },
                    form.lastName.trim().ifBlank { "_" },
                    form.email.trim().ifBlank { "_" },
                ),
            ).fold(
                onSuccess = {
                    updateState { it.copy(dialogState = OpenQuestionsState.DialogState.None) }
                    sideEffectFlow.emit(OpenQuestionsEffect.ShowToast("השאלה נשלחה בהצלחה, תודה!"))
                },
                onFailure = {
                    updateState { state ->
                        state.copy(
                            dialogState = (state.dialogState as? OpenQuestionsState.DialogState.AddQuestion)
                                ?.copy(isSubmitting = false) ?: state.dialogState
                        )
                    }
                    sideEffectFlow.emit(OpenQuestionsEffect.ShowToast("שגיאה בשליחה, נסה שוב מאוחר יותר"))
                },
            )
        }
        return currentState
    }

    // ─── Create Your Own Q&A (load from user's Google Sheet) ─────────────────

    private fun handleCreateYourOwnQnA(): OpenQuestionsState =
        currentState.copy(
            dialogState = OpenQuestionsState.DialogState.CustomSheetInput(
                url = settings.getString(key = Constants.OPEN_QUESTIONS_USER_SPREADSHEET, defaultValue = ""),
            )
        )

    private fun handleCustomSheetUrlChanged(url: String): OpenQuestionsState {
        val current = currentState.dialogState as? OpenQuestionsState.DialogState.CustomSheetInput
            ?: return currentState
        return currentState.copy(dialogState = current.copy(url = url))
    }

    private fun handleSubmitCustomSheetUrl(): OpenQuestionsState {
        val form = currentState.dialogState as? OpenQuestionsState.DialogState.CustomSheetInput
            ?: return currentState
        val url = form.url.trim()
        if (url.isBlank()) {
            launchEffect { sideEffectFlow.emit(OpenQuestionsEffect.ShowToast("יש להזין כתובת גיליון")) }
            return currentState
        }
        val sheetId = try {
            SheetUtil.getSpreadsheetId(url)
        } catch (_: Exception) {
            launchEffect { sideEffectFlow.emit(OpenQuestionsEffect.ShowToast("כתובת הגיליון אינה תקינה")) }
            return currentState
        }

        launchEffect {
            updateState {
                it.copy(
                    dialogState = (it.dialogState as? OpenQuestionsState.DialogState.CustomSheetInput)
                        ?.copy(isLoading = true) ?: it.dialogState
                )
            }
            getGoogleScriptDataUseCase.invoke(sheetId).fold(
                onSuccess = { sheetNames ->
                    if (sheetNames == null) {
                        updateState { it.copy(dialogState = OpenQuestionsState.DialogState.None) }
                        sideEffectFlow.emit(OpenQuestionsEffect.ShowToast("לא נמצאו גיליונות"))
                        return@launchEffect
                    }
                    settings.putString(Constants.OPEN_QUESTIONS_USER_SPREADSHEET, url)
                    val names = sheetNames.sheetNamesList
                    if (names.size == 1) {
                        updateState { it.copy(dialogState = OpenQuestionsState.DialogState.None) }
                        loadCustomSheetQuestions(url = url, position = 0, sheetTitle = names[0])
                    } else {
                        updateState {
                            it.copy(
                                pendingCustomSheetUrl = url,
                                dialogState = OpenQuestionsState.DialogState.TableOfContents(
                                    tableOfContentsItems = sheetNames.numberedSheetNames
                                        .map { name -> AlternateRowData(title = name) }
                                        .toImmutableList(),
                                ),
                            )
                        }
                    }
                },
                onFailure = { error ->
                    updateState {
                        it.copy(
                            dialogState = (it.dialogState as? OpenQuestionsState.DialogState.CustomSheetInput)
                                ?.copy(isLoading = false) ?: it.dialogState
                        )
                    }
                    sideEffectFlow.emit(OpenQuestionsEffect.ShowToast(error.message.orEmpty()))
                },
            )
        }
        return currentState
    }

    private fun handleCustomSheetPositionSelected(url: String, position: Int): OpenQuestionsState {
        launchEffect {
            updateState { it.copy(pendingCustomSheetUrl = "", isLoading = true) }
            loadCustomSheetQuestions(url = url, position = position, sheetTitle = null)
        }
        return currentState
    }

    private suspend fun loadCustomSheetQuestions(url: String, position: Int, sheetTitle: String?) {
        getGoogleDriveDataUseCase(
            sheetId = url.getId(),
            sheetNumber = position + 1,
            format = SheetUtil.JSON,
        ).fold(
            onSuccess = { sheet ->
                if (sheet == null) {
                    updateState { it.copy(isLoading = false) }; return
                }
                val items = buildQuestionsFromSheetUseCase(feed = sheet.feed)
                val title = sheetTitle ?: sheet.feed.title
                reloadShownQuestions = {
                    launchEffect {
                        updateState { it.copy(isLoading = true) }
                        loadCustomSheetQuestions(url = url, position = position, sheetTitle = sheetTitle)
                    }
                }
                updateState {
                    it.copy(
                        isLoading = false,
                        questionItems = items.toImmutableList(),
                        topicTitle = "נושא: *$title* (${items.size} שאלות)",
                        questionsShownCount = items.size,
                        dialogState = OpenQuestionsState.DialogState.None,
                    )
                }
                sideEffectFlow.emit(OpenQuestionsEffect.ScrollToTop)
            },
            onFailure = { error ->
                updateState { it.copy(isLoading = false) }
                sideEffectFlow.emit(OpenQuestionsEffect.ShowToast(error.message.orEmpty()))
            },
        )
    }

    // ─── Dialogs ─────────────────────────────────────────────────────────────

    private fun handleHelpClicked(): OpenQuestionsState {
        launchEffect {
            val content = assetReader.readFile(
                fileName = "other${assetReader.fileSeparator}open_questionsGuide.txt"
            )
            val totalQuestions = currentState.questionItems.size
            val formattedContent = runCatching {
                content.formatString(totalQuestions, "")
            }.getOrDefault(content)

            val shouldSkipReadMe = settings.getBoolean(
                key = SKIP_README_PREF,
                defaultValue = true,
            )
            updateState {
                it.copy(
                    dialogState = OpenQuestionsState.DialogState.Help(
                        isDontShowAgainChecked = shouldSkipReadMe,
                        content = formattedContent,
                    )
                )
            }
        }
        return currentState
    }

    private fun handleToggleHelp(shouldShowNextTime: Boolean): OpenQuestionsState {
        settings.putBoolean(SKIP_README_PREF, !shouldShowNextTime)
        return currentState.copy(dialogState = OpenQuestionsState.DialogState.None)
    }

    private fun handleHideDialog(): OpenQuestionsState =
        currentState.copy(dialogState = OpenQuestionsState.DialogState.None)

    private fun handleShowTableOfContents(items: ImmutableList<AlternateRowData>): OpenQuestionsState =
        currentState.copy(
            dialogState = OpenQuestionsState.DialogState.TableOfContents(items),
        )

    private fun handleShowChapterDialog(chapterData: ChapterData): OpenQuestionsState =
        currentState.copy(
            dialogState = OpenQuestionsState.DialogState.ChapterDialog(chapterData),
        )

    private fun handleShowGeneralMessage(title: String, content: String): OpenQuestionsState =
        currentState.copy(
            dialogState = OpenQuestionsState.DialogState.GeneralMessage(
                title = title,
                content = content,
            ),
        )

    // ─── Helpers ─────────────────────────────────────────────────────────────

    private suspend fun loadQuestionsForTopic(
        spreadsheetId: String,
        sheetId: String,
        topicName: String,
    ) {
        var loadedItems: List<QuestionItem> = emptyList()
        var loadedTitle = ""

        getQuestionsAndAnswersUseCase.invoke(
            spreadsheetId = spreadsheetId,
            sheetId = sheetId,
            topicName = topicName,
            onItems = { items -> loadedItems = items },
            onTitle = { title -> loadedTitle = title },
        ).fold(
            onSuccess = {
                reloadShownQuestions = {
                    launchEffect {
                        updateState { it.copy(isLoading = true) }
                        loadQuestionsForTopic(spreadsheetId = spreadsheetId, sheetId = sheetId, topicName = topicName)
                    }
                }
                updateState {
                    it.copy(
                        isLoading = false,
                        questionItems = loadedItems.toImmutableList(),
                        topicTitle = loadedTitle,
                        questionsShownCount = loadedItems.size,
                        dialogState = OpenQuestionsState.DialogState.None,
                    )
                }
                sideEffectFlow.emit(OpenQuestionsEffect.ScrollToTop)
            },
            onFailure = { error ->
                updateState { it.copy(isLoading = false) }
                if (error.isInternetError()) {
                    sideEffectFlow.emit(OpenQuestionsEffect.ShowNoInternetError)
                }
            },
        )
    }

    private fun buildTopicsDialogItems(topics: List<TopicItem>): ImmutableList<AlternateRowData> =
        buildList {
            add(AlternateRowData(title = PAIRS_ITEM_TITLE, isBold = true))
            topics.forEach { add(AlternateRowData(title = it.topic)) }
        }.toImmutableList()

    private fun loadCachedTopics(): ImmutableList<TopicItem> {
        val json = settings.getString(
            key = Constants.PREF_OPEN_QUESTIONS_TOPICS_JSON,
            defaultValue = EMPTY_JSON_ARRAY,
        )
        return if (json == EMPTY_JSON_ARRAY) {
            emptyList<TopicItem>().toImmutableList()
        } else {
            runCatching { json.convertToDataClass<List<TopicItem>>() }
                .getOrDefault(emptyList())
                .toImmutableList()
        }
    }

    companion object {
        private fun QuestionItemJsonVisibility.toQuestionItem(num: Int): QuestionItem = QuestionItem(
            question = questionItemJson.question.orEmpty(),
            answer = questionItemJson.answer.orEmpty(),
            author = questionItemJson.author.orEmpty(),
            book = questionItemJson.book.orEmpty(),
            chapter = questionItemJson.chapter.orEmpty(),
            verse = questionItemJson.verse.orEmpty(),
            isAnswerVisible = answerIsVisible,
            num = num,
        )

        private const val CACHE_TTL_MS = 24 * 60 * 60 * 1000L // 24 hours
        const val SKIP_README_PREF = "skipReadMeOpenQuestionsV2"
        private const val PAIRS_ITEM_INDEX = 0
        private const val PREDEFINED_ITEMS_COUNT = 1
        private const val PAIRS_ITEM_TITLE = "שאלות מהתאמת זוגות"

        private const val MULTIPLE_RANDOM_VERSES_TITLE = "קוקטייל פסוקים אקראיים"

        // Special topic name markers: SPECIAL_TOPIC_* in :appContent's OpenQuestionsMarkers.kt

        // Google Form field keys for submitting user questions
        private const val ADD_Q_KEY_QUESTION = "entry.473901271"
        private const val ADD_Q_KEY_ANSWER = "entry.967073937"
        private const val ADD_Q_KEY_FIRST_NAME = "entry.48047135"
        private const val ADD_Q_KEY_LAST_NAME = "entry.563652295"
        private const val ADD_Q_KEY_EMAIL = "entry.265596540"
    }
}
